import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import {
  ColunaConfiguracao,
  ConfiguracaoColunasModalComponent,
} from '../../../shared/components/configuracao-colunas-modal/configuracao-colunas-modal.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import {
  ExtracaoDadosData,
  ExtracaoDadosModalComponent,
  FiltroExtracao,
} from '../../../shared/components/extracao-dados-modal/extracao-dados-modal.component';
import { OrigemPedidoExportacaoEnum } from '../../../shared/enums/batch-extracao-dados/origem-pedido-exportacao.enum';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { normalizarValorOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { TipoExtracaoEnum } from '../../../shared/enums/batch-extracao-dados/tipo-extracao.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { ControleEstoqueListaRefinamentoEnum } from '../../../shared/enums/produto/controle-estoque-lista-refinamento.enum';
import { ControleEstoqueResumoFiltroEnum } from '../../../shared/enums/produto/controle-estoque-resumo-filtro.enum';
import { ControleEstoqueTipoMovimentoFiltroEnum } from '../../../shared/enums/produto/controle-estoque-tipo-movimento-filtro.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { EtiquetaLookupService } from '../../../shared/services/apis/etiqueta/etiqueta-lookup.service';
import { ControleEstoqueService } from '../../../shared/services/apis/produto/controle-estoque.service';
import { LookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { ControleEstoqueListaRequest } from '../../../shared/services/requests/produto/controle-estoque-lista.request';
import { GetAllControleEstoqueResponse } from '../../../shared/services/responses/produto/get-all-controle-estoque.response';
import { LocalizacaoEstoque } from '../../../shared/services/models/produto/localizacao-estoque.model';
import { ListaPrecoStruct } from '../../../shared/services/structs/produto/lista-preco.struct';
import { ControleEstoqueListStruct } from '../../../shared/services/structs/produto/controle-estoque-list.struct';
import { ControleEstoqueLocalStruct } from '../../../shared/services/structs/produto/controle-estoque-local.struct';
import { ControleEstoqueResumoStruct } from '../../../shared/services/structs/produto/controle-estoque-resumo.struct';
import { ProdutoListStruct } from '../../../shared/services/structs/produto/produto-list.struct';
import { UtilService } from '../../../shared/services/util.service';
import {
  ImprimirEtiquetasDialogComponent,
  ImprimirEtiquetasDialogData,
} from '../../../shared/components/imprimir-etiquetas-dialog/imprimir-etiquetas-dialog.component';
import { EtiquetaLookupItemStruct } from '../../../shared/services/structs/etiqueta/etiqueta-lookup-item.struct';
import { ImpressaoControleEstoqueComponent } from './dialogs/impressao-controle-estoque/impressao-controle-estoque.component';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';
import { OrigemConsultaControleEstoqueEnum } from '../../../shared/enums/produto/origem-consulta-controle-estoque.enum';

interface ControleEstoqueDisplayRow {
  data: ControleEstoqueListStruct;
  local?: ControleEstoqueLocalStruct;
  isLocal: boolean;
}

@Component({
  selector: 'app-controle-estoque',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatBadgeModule,
    MatTooltipModule,
    MatSelectModule,
    MatSortModule,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
    RouterModule,
    AbntPipe,
  ],
  templateUrl: './controle-estoque.component.html',
  styleUrls: ['./controle-estoque.component.css'],
})
export class ControleEstoqueComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_ControleEstoque;
  public _refinamentoEnum = ControleEstoqueListaRefinamentoEnum;
  public _tipoMovimentoFiltroEnum = ControleEstoqueTipoMovimentoFiltroEnum;
  public _resumoFiltroEnum = ControleEstoqueResumoFiltroEnum;

  public model: FormGroup;
  public filtroRefinar: number | null = null;
  public resumoFiltro: ControleEstoqueResumoFiltroEnum = ControleEstoqueResumoFiltroEnum.Todos;
  public resumo: ControleEstoqueResumoStruct = new ControleEstoqueResumoStruct();

  public _isLoading: boolean = false;
  public _isLoadingAction: boolean = false;
  public _response: GetAllControleEstoqueResponse | undefined;
  public _dataSource = new MatTableDataSource<ControleEstoqueListStruct>();
  public _flatDataSource = new MatTableDataSource<ControleEstoqueDisplayRow>();
  public _paginaAtual: number = 0;
  public _ultimaPagina: boolean = false;
  public expandedElement: ControleEstoqueListStruct | null = null;

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._flatDataSource.sort = sort;
    this._flatDataSource.sortingDataAccessor = (row, property): string | number =>
      this.getSortValue(row, property);
  }

  public listaPreco: ListaPrecoStruct[] = [];
  public listaLocalizacaoEstoque: LocalizacaoEstoque[] = [];
  public podeVisualizarCusto: boolean = false;

  public displayedColumns: string[] = [];
  public todosCampos: ColunaConfiguracao[] = [
    { def: 'produto', label: 'Produto', visible: true, fixed: false },
    { def: 'codigoSku', label: 'SKU', visible: true, fixed: false },
    { def: 'local', label: 'Local', visible: true, fixed: false },
    { def: 'unidade', label: 'Unidade', visible: true, fixed: false },
    { def: 'reservado', label: 'Reservado', visible: true, fixed: false },
    { def: 'disponivel', label: 'Disponível', visible: true, fixed: false },
    { def: 'custoMedio', label: 'Custo médio', visible: true, fixed: false },
    { def: 'custoTotal', label: 'Custo total', visible: true, fixed: false },
    { def: 'preco', label: 'Preço', visible: true, fixed: false },
    { def: 'actions', label: 'Ações', visible: true, fixed: true, pinnedEnd: true },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private controleEstoqueService: ControleEstoqueService,
    private lookupService: LookupService,
    private etiquetaLookupService: EtiquetaLookupService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.atualizarColunasVisiveis();

    this.model = this.formBuilder.group({
      pesquisa: [''],
      idTipoMovimentoFiltro: [ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro],
    });

    this.carregarLookups();
    this.loadFilters();
    this.pesquisar();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/suprimentos/controle-estoque',
    ]);
  }

  public voltar(): void {
    this.location.back();
  }

  public pesquisar(): void {
    if (this._isLoading) return;

    this._isLoading = true;
    const request = this.getFiltrosRequest();

    this.controleEstoqueService.getAll(request, this._paginaAtual).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.podeVisualizarCusto = response.podeVisualizarCusto;
        this.atualizarColunasVisiveis();
        this.resumo = response.resumo || new ControleEstoqueResumoStruct();

        if (!this._response) {
          this._response = response;
          this._dataSource.data = response.list || [];
        } else {
          this._response.list.push(...(response.list || []));
          this._dataSource.data = this._response.list;
        }

        this.reconstruirFlatDataSource();
        this._ultimaPagina = !response.list?.length || this._dataSource.data.length >= response.fullListSize;
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
      },
    });
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this._response = undefined;
    this._dataSource.data = [];
    this._flatDataSource.data = [];
    this._ultimaPagina = false;
    this.expandedElement = null;
    this.pesquisar();
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  public aplicarFiltros(): void {
    this.clickPesquisar();
  }

  public limparFiltros(): void {
    this.model.patchValue({
      pesquisa: '',
      idTipoMovimentoFiltro: ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro,
    });
    this.filtroRefinar = null;
    this.resumoFiltro = ControleEstoqueResumoFiltroEnum.Todos;
    this.clickPesquisar();
  }

  public selecionarResumo(filtro: ControleEstoqueResumoFiltroEnum): void {
    this.resumoFiltro = filtro;
    this.clickPesquisar();
  }

  public toggleRow(row: ControleEstoqueDisplayRow): void {
    if (row.isLocal) return;

    // Linha do produto: expande/colapsa os locais (quando há mais de um).
    if (!this.isRowExpandivel(row)) return;

    this.expandedElement = this.expandedElement === row.data ? null : row.data;
    this.reconstruirFlatDataSource();
  }

  public toggleMobileProduto(produto: ControleEstoqueListStruct): void {
    this.expandedElement = this.expandedElement === produto ? null : produto;
  }

  public isRowExpandivel(row: ControleEstoqueDisplayRow): boolean {
    return !row.isLocal && row.data.quantidadeLocais > 1;
  }

  public verMovimentacoes(produto: ControleEstoqueListStruct): void {
    this.router.navigate(['/suprimentos/controle-estoque-detalhe', produto.idProduto]);
  }

  public reconstruirFlatDataSource(): void {
    const flat: ControleEstoqueDisplayRow[] = [];

    for (const produto of this._dataSource.data) {
      flat.push({ data: produto, isLocal: false });

      if (this.expandedElement !== produto) continue;

      // Nível 2 = locais do produto (quando há mais de um).
      if (produto.locais?.length > 1) {
        for (const local of produto.locais) {
          flat.push({ data: produto, local, isLocal: true });
        }
      }
    }

    this._flatDataSource.data = flat;
  }

  public getFiltrosRequest(): ControleEstoqueListaRequest {
    const request = new ControleEstoqueListaRequest();
    request.origem = OrigemConsultaControleEstoqueEnum.ControleEstoque;
    request.searchText = this.model.get('pesquisa')?.value;
    request.idTipoMovimentoFiltro =
      this.model.get('idTipoMovimentoFiltro')?.value ?? ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro;
    request.idResumoFiltro = this.resumoFiltro;

    if (this.filtroRefinar) {
      request.idControleEstoqueListaRefinamento = this.filtroRefinar;
    }

    return request;
  }

  public get qtdFiltrosAvancados(): number {
    const tipoMovimento = this.model.get('idTipoMovimentoFiltro')?.value;
    return tipoMovimento && tipoMovimento !== ControleEstoqueTipoMovimentoFiltroEnum.SemFiltro ? 1 : 0;
  }

  public getLabelTipoMovimento(): string {
    const tipoMovimento = this.model.get('idTipoMovimentoFiltro')?.value;

    if (tipoMovimento === ControleEstoqueTipoMovimentoFiltroEnum.Entrada) return 'Entrada';
    if (tipoMovimento === ControleEstoqueTipoMovimentoFiltroEnum.Saida) return 'Saída';
    if (tipoMovimento === ControleEstoqueTipoMovimentoFiltroEnum.Inventario) return 'Inventário';

    return 'Sem filtro';
  }

  public getLocalizacao(row: ControleEstoqueDisplayRow): string {
    if (row.isLocal) {
      return row.local?.localizacao || row.local?.codigoLocalizacao || 'Local não informado';
    }

    if (row.data.quantidadeLocais > 1) {
      return `${row.data.quantidadeLocais} locais`;
    }

    return row.data.localizacao || 'Sem local';
  }

  public getReservado(row: ControleEstoqueDisplayRow): number {
    return row.isLocal ? (row.local?.saldoReservado ?? 0) : row.data.saldoReservado;
  }

  public getDisponivel(row: ControleEstoqueDisplayRow): number {
    return row.isLocal ? (row.local?.saldoDisponivel ?? 0) : row.data.saldoDisponivel;
  }

  private getSortValue(row: ControleEstoqueDisplayRow, property: string): string | number {
    switch (property) {
      case 'produto':
        return this.normalizarTextoOrdenacao(row.data.descricao);
      case 'codigoSku':
        return this.normalizarTextoOrdenacao(row.data.codigoSku);
      case 'local':
        return this.getLocalOrdenacao(row.data);
      case 'unidade':
        return this.normalizarTextoOrdenacao(row.data.unidade);
      case 'reservado':
        return row.data.saldoReservado ?? 0;
      case 'disponivel':
        return row.data.saldoDisponivel ?? 0;
      case 'custoMedio':
        return row.data.custoMedio ?? 0;
      case 'custoTotal':
        return row.data.custoTotal ?? 0;
      case 'preco':
        return row.data.preco ?? 0;
      default:
        return '';
    }
  }

  private getLocalOrdenacao(produto: ControleEstoqueListStruct): string {
    if (produto.quantidadeLocais > 1) {
      return `${produto.quantidadeLocais.toString().padStart(10, '0')} locais`;
    }

    return this.normalizarTextoOrdenacao(produto.localizacao);
  }

  private normalizarTextoOrdenacao(valor?: string): string {
    return normalizarValorOrdenacao(valor || '') as string;
  }

  public getResumoValor(filtro: ControleEstoqueResumoFiltroEnum): number {
    if (filtro === ControleEstoqueResumoFiltroEnum.Disponivel) return this.resumo.disponivel;
    if (filtro === ControleEstoqueResumoFiltroEnum.EstoqueBaixo) return this.resumo.estoqueBaixo;
    if (filtro === ControleEstoqueResumoFiltroEnum.EstoqueZerado) return this.resumo.estoqueZerado;
    if (filtro === ControleEstoqueResumoFiltroEnum.Reservado) return this.resumo.reservado;
    return this.resumo.todos;
  }

  public executarAcaoIndisponivel(rotina: string): void {
    this.alertService.show('Atenção', `${rotina} será implementado em uma próxima etapa.`, AlertType.warning);
  }

  public navegarParaTransferencia(): void {
    this.router.navigate(['/suprimentos/transferencia-estoque']);
  }

  public imprimirRelatorio(): void {
    this.dialog.open(ImpressaoControleEstoqueComponent, {
      data: { filtros: this.getFiltrosRequest() },
      maxWidth: '90vw',
      disableClose: false,
    });
  }

  public exportarControleEstoque(): void {
    if (this._isLoadingAction) return;

    const filtrosMapeados = this.getFiltrosExtracao();
    const dialogData: ExtracaoDadosData = {
      titulo: 'Exportação de Controle de Estoque',
      tipoExtracao: TipoExtracaoEnum.ControleEstoqueCSV,
      origemPedidoExportacao: OrigemPedidoExportacaoEnum.SuprimentosControleEstoque,
      filtros: filtrosMapeados,
      possuiAutomacao: false,
    };

    this.dialog.open(ExtracaoDadosModalComponent, {
      data: dialogData,
      panelClass: 'extracao-dados-panel',
      disableClose: true,
    });
  }

  public configurarColunas(): void {
    const dialogRef = this.dialog.open(ConfiguracaoColunasModalComponent, {
      maxWidth: '700px',
      data: { colunas: this.getColunasPermitidas() },
    });

    dialogRef.afterClosed().subscribe((result: ColunaConfiguracao[]) => {
      if (!result) return;

      const colunasNaoPermitidas = this.todosCampos.filter(coluna => !this.colunaPermitida(coluna.def));
      this.todosCampos = [...result, ...colunasNaoPermitidas];
      this.atualizarColunasVisiveis();
    });
  }

  public imprimirEtiquetas(produto: ControleEstoqueListStruct): void {
    if (this._isLoadingAction) return;

    this._isLoadingAction = true;
    this.etiquetaLookupService.listEtiquetaSelect().subscribe({
      next: res => {
        this._isLoadingAction = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        if (!res.list || res.list.length === 0) {
          this.exibirAlertaSemConfiguracao();
          return;
        }

        this.abrirModalImprimirEtiquetas([this.mapearProdutoEtiqueta(produto)], res.list);
      },
      error: () => {
        this._isLoadingAction = false;
        this.alertService.show('Erro', 'Falha ao verificar configurações de etiqueta.', AlertType.error);
      },
    });
  }

  private atualizarColunasVisiveis(): void {
    this.displayedColumns = this.getColunasPermitidas()
      .filter(coluna => coluna.visible)
      .map(coluna => coluna.def);
  }

  private getColunasPermitidas(): ColunaConfiguracao[] {
    return this.todosCampos.filter(coluna => this.colunaPermitida(coluna.def));
  }

  private colunaPermitida(coluna: string): boolean {
    return !((coluna === 'custoMedio' || coluna === 'custoTotal') && !this.podeVisualizarCusto);
  }

  private carregarLookups(): void {
    this.lookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaPreco = res.listaPreco || [];
          this.listaLocalizacaoEstoque = res.listaLocalizacaoEstoque || [];
        }
      },
    });
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  private getFiltrosExtracao(): FiltroExtracao[] {
    const request = this.getFiltrosRequest();
    const filtros: FiltroExtracao[] = [
      {
        label: 'Tipo',
        formControlName: 'idTipoMovimentoFiltro',
        valor: request.idTipoMovimentoFiltro,
        valorExibicao: this.getLabelTipoMovimento(),
      },
      {
        label: 'Resumo',
        formControlName: 'idResumoFiltro',
        valor: request.idResumoFiltro,
        valorExibicao: this.getLabelResumoFiltro(request.idResumoFiltro),
      },
      {
        label: 'Incluir custos',
        formControlName: 'incluirCustos',
        valor: this.podeVisualizarCusto,
        valorExibicao: this.podeVisualizarCusto ? 'Sim' : 'Não',
      },
    ];

    if (request.searchText) {
      filtros.push({
        label: 'Pesquisa',
        formControlName: 'searchText',
        valor: request.searchText,
      });
    }

    if (request.idControleEstoqueListaRefinamento) {
      filtros.push({
        label: 'Refinamento',
        formControlName: 'idControleEstoqueListaRefinamento',
        valor: request.idControleEstoqueListaRefinamento,
        valorExibicao: this.getLabelRefinamento(request.idControleEstoqueListaRefinamento),
      });
    }

    return filtros;
  }

  public getLabelResumoFiltro(filtro: number): string {
    if (filtro === ControleEstoqueResumoFiltroEnum.Disponivel) return 'Disponível';
    if (filtro === ControleEstoqueResumoFiltroEnum.EstoqueBaixo) return 'Estoque baixo';
    if (filtro === ControleEstoqueResumoFiltroEnum.EstoqueZerado) return 'Estoque zerado';
    if (filtro === ControleEstoqueResumoFiltroEnum.Reservado) return 'Reservado';
    return 'Todos';
  }

  public getResumoCorClass(filtro: ControleEstoqueResumoFiltroEnum): string {
    if (filtro === ControleEstoqueResumoFiltroEnum.Disponivel) return 'card-filtro-verde';
    if (filtro === ControleEstoqueResumoFiltroEnum.EstoqueBaixo) return 'card-filtro-laranja';
    if (filtro === ControleEstoqueResumoFiltroEnum.EstoqueZerado) return 'card-filtro-vinho';
    if (filtro === ControleEstoqueResumoFiltroEnum.Reservado) return 'card-filtro-amarelo';
    return 'card-filtro-cinza';
  }

  private getLabelRefinamento(refinamento: number): string {
    if (refinamento === ControleEstoqueListaRefinamentoEnum.Descricao) return 'Descrição';
    if (refinamento === ControleEstoqueListaRefinamentoEnum.CodigoSku) return 'SKU';
    if (refinamento === ControleEstoqueListaRefinamentoEnum.GtinEan) return 'EAN';
    if (refinamento === ControleEstoqueListaRefinamentoEnum.CodigoFornecedor) return 'Código do fornecedor';
    if (refinamento === ControleEstoqueListaRefinamentoEnum.NumeroSerie) return 'Número de série';
    return 'Não refinar';
  }

  private mapearProdutoEtiqueta(produto: ControleEstoqueListStruct): ProdutoListStruct {
    const item = new ProdutoListStruct();
    item.idProduto = produto.idProduto;
    item.descricao = produto.descricao;
    item.codigoSku = produto.codigoSku;
    item.gtinEan = produto.gtinEan;
    item.unidade = produto.unidade;
    item.preco = produto.preco;
    item.saldoFisico = produto.saldoFisico;
    item.saldoDisponivel = produto.saldoDisponivel;
    item.isVariacao = false;
    item.qtdVariacoes = 0;
    item.isKit = false;
    item.isControlaLotes = produto.isControlaLotes;
    item.isControlaSeries = produto.isControlaSeries;
    item.variacoes = [];

    return item;
  }

  private abrirModalImprimirEtiquetas(
    listaProdutos: ProdutoListStruct[],
    listaConfiguracoes: EtiquetaLookupItemStruct[]
  ): void {
    const dialogData: ImprimirEtiquetasDialogData = {
      listaProdutos,
      listaConfiguracoes,
      listaPrecos: this.listaPreco,
      listaLocalizacaoEstoque: this.listaLocalizacaoEstoque,
    };

    this.dialog.open(ImprimirEtiquetasDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      data: dialogData,
    });
  }

  private exibirAlertaSemConfiguracao(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Configuração necessária',
        description:
          'Para imprimir as etiquetas é necessário realizar as configurações. Você pode fazer isso acessando Configurações > Configurações das etiquetas ou clicando no botão configurar etiquetas.',
        buttonConfirm: 'Configurar etiquetas',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.router.navigate(['/configuracoes/gerais/etiquetas']);
      }
    });
  }
}
